import { i18n, PREFECTURE_OPTIONS } from "shared";
import { theme } from "shared/src/theme";
import { styled } from "styled-components";
import { useRouter } from "next/router";
import ReactPlayer from "react-player/youtube";
import { useQuery } from "react-query";
import { getArticleDetails } from "../../service/article";

import { Collapse, Result, Skeleton } from "antd";
import { GetServerSideProps } from "next";
import { useTranslation } from "next-i18next";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import Head from "next/head";
import { useEffect, useState } from "react";
import DownIcon from "../../assets/downIcon.svg";
import UpIcon from "../../assets/upIcon.svg";
import { SimilarArticles } from "../../components";
import { debounce } from "../../utils/debounce";

const Wrapper = styled.div`
  max-width: 1440px;
  position: relative;
  padding: 128px 0px 0px;
  margin: auto;
  @media (max-width: 1095px) {
    padding-top: 112px;
  }
  @media (max-width: 360px) {
    padding-top: 92px;
  }

  & .quill {
    margin-top: 19px;
  }

  & > h1 {
    ${theme.typography.headline2}
    margin-bottom: 16px;
  }
  & p {
    margin-bottom: 0px;
  }
  & .space,
  .content {
    @media (max-width: 1550px) {
      padding: 0px 5%;
    }
    @media (max-width: 1440px) {
      padding: 0px 9.02% 0px 8.33%;
    }
    @media (max-width: 1245px) {
      padding: 0px 5%;
    }
    @media (max-width: 1150px) {
      padding: 0px 3%;
    }
    @media (max-width: 768px) {
      padding: 0px 16px;
    }
  }
`;

const Slug = styled.p`
  margin-bottom: 8px;
  ${theme.typography.body}
  color: ${theme.grey2};
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${theme.typography.bodySmall}
  margin-bottom: 8px;
  & span {
    margin-right: 18px;
    display: block;
  }
  color: ${theme.buttonRed};
`;

const Player = styled.div`
  margin-bottom: 16px;
  display: flex;
  justify-content: center;
  & .container {
    & img {
      width: 100%;
    }
  }
`;

const Content = styled.div`
  padding-bottom: 64px;
  & h1 {
    ${theme.typography.subtitle1}
    font-size: 20px !important;
    border-left: 4px solid ${theme.buttonRed};
    padding-left: 8px;
    padding-right: 8px;
    margin: 6px 0px 16px;
    width: fit-content;
    background-color: ${theme.gray4};
  }
  & a {
    word-break: break-all;
  }
  & p,
  li {
    ${theme.typography.body}
    color: ${theme.gray0};
    text-align: justify;
  }
  & .ql-video {
    width: 791px;
    height: 447px;
    margin: auto;
  }
  & .content {
    & img {
      margin: 0 auto;
      max-width: 100%;
    }
  }
`;

const TableOfContent = styled(Collapse)`
  background-color: ${theme.gray4};
  padding: 16px 16px 14px;
  border-radius: 8px;

  & .ant-collapse-header,
  .ant-collapse-content-box {
    padding: 0px !important;
  }
  & .ant-collapse-content-box {
    margin-top: 12px !important;
  }

  & .ant-collapse-header {
    ${theme.typography.subtitle2}
    color: ${theme.buttonRed} !important;
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    width: fit-content;

    & svg {
      position: unset !important;
      margin-left: 14px !important;
      transform: none !important;
    }
  }

  & a {
    display: block;
    ${theme.typography.body}
    color: ${theme.gray1};
    text-decoration: underline;
    margin-bottom: 8px;

    &:hover {
      color: ${theme.buttonRed};
    }
  }

  & a.active {
    color: ${theme.buttonRed};
  }

  & ul {
    padding-left: 20px;
  }

  & li {
    text-align: left;
  }

  @media (min-width: 769px) {
    & ul {
      padding-left: 2px;
      list-style: none;
    }
  }
`;

const LoadingWrapper = styled.div`
  & .ant-skeleton-element {
    width: 100%;
    & > * {
      width: 100%;
      height: 250px;
    }
  }
`;

const Sticky = styled.div`
  position: sticky;
  top: 90px;
  background-color: #fff;
  z-index: 1221;
  width: 100%;
  padding-bottom: 10px;
  @media (max-width: 1095px) {
    top: 74px;
  }
  @media (max-width: 360px) {
    top: 54px;
  }

  & > div {
    @media (max-width: 1550px) {
      margin: 0px 5%;
    }
    @media (max-width: 1440px) {
      margin: 0px 9.02% 0px 8.33%;
    }
    @media (max-width: 1245px) {
      margin: 0px 5%;
    }
    @media (max-width: 1150px) {
      margin: 0px 3%;
    }
    @media (max-width: 768px) {
      margin: 0px 16px;
    }
  }
`;

const ArticleDetails = (props) => {
  const router = useRouter();
  const { id, admin } = router.query;
  const { t } = useTranslation("common");
  const [headings, setHeadings] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState("");

  const { data, isLoading, isError } = useQuery<any, any>(
    ["article-details", id],
    () => getArticleDetails(id, i18n.language, admin),
    {
      initialData: props.content,
      refetchOnWindowFocus: false,
      enabled: !!id,
    }
  );

  const getNode = () => {
    setTimeout(() => {
      const x =
        typeof window !== "undefined" && document.getElementById("content");
      if (x) {
        const allHeadings = [];
        const y = x.getElementsByTagName("h1") as any;
        for (let i = 0; i < y.length; i++) {
          y[i].id = `heading${i}`;
          allHeadings.push({
            id: `heading${i}`,
            title: y[i].innerHTML,
          });
        }
        if (data && headings.length === 0) {
          setHeadings([...allHeadings]);
        }
        return y.length;
      }
    }, [1000]);
    return "";
  };

  const scrollToID = (key) => {
    const element = document.getElementById(key);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  const debounceFunction = debounce((handleFunction: any) => {
    handleFunction();
    return null;
  }, 200);

  const debouncedScroll = () => {
    debounceFunction(handleScroll);
  };

  const isInViewPort = (elem) => {
    const offset = -150;
    const top = elem.getBoundingClientRect().top;
    return top + offset >= 0 && top - offset <= window.innerHeight;
  };

  const handleScroll = () => {
    const negativeOffsetY = -150;
    const scrollPosition = window.scrollY + negativeOffsetY;

    if (scrollPosition < 0) {
      setSelectedMenu("");
      return;
    }
    headings.map((heading) => {
      const element = document.getElementById(heading.id);
      if (element && isInViewPort(element)) {
        setSelectedMenu(heading.id);
      }
    });
  };

  useEffect(() => {
    debounceFunction(handleScroll);
    window.addEventListener("scroll", debouncedScroll);
    return () => {
      window.removeEventListener("scroll", debouncedScroll);
    };
  }, [selectedMenu, headings]);

  const GetPrefecture = (code) => {
    const name = PREFECTURE_OPTIONS?.find((pre) => pre.value === code)?.label;
    return name;
  };

  if (isError) {
    return (
      <Wrapper>
        <Result status="404" title={t("Record not found")} />
      </Wrapper>
    );
  }

  return (
    <>
      <Head>
        <title>
          {props?.content?.data?.title + "-" + t("Kansai Startup Ecosystem")}
        </title>
        <meta
          key="facilities_description_image"
          property="og:image"
          content={props?.content?.data?.coverImage}
        />
        <meta
          key="facilities_og_url"
          property="og:url"
          content={`${props?.content?.data?.siteUrl}${props?.data?.id}`}
        />
        <meta
          key="facility_title"
          property="og:title"
          content={props?.content?.data?.title}
        />
        <meta
          key="facility_description"
          property="og:description"
          content={props?.content?.data?.overview}
        />

        <meta
          key="facility-Description"
          name="description"
          content={props?.content?.data?.overview}
        />
        <meta
          name="keywords"
          content="Articles,Deeptech valley,Deeptech,関西ディープテック、関西SU,関西スタートアップ,関西スタートアップエコシステム,関西広域連合,kansai startup,japan kansai startup ecosystem,kansai-ecosystem,deeptech kansai"
        />
      </Head>
      <Wrapper>
        {isLoading ? (
          <LoadingWrapper className="space">
            <Skeleton active paragraph={{ rows: 2 }} />
            <Skeleton.Button active />
            <Skeleton active paragraph={{ rows: 3 }} />
          </LoadingWrapper>
        ) : (
          <>
            <div className="space">
              <Slug>
                {GetPrefecture(props?.content?.data?.city?.toString())}
              </Slug>
              <h1>{props?.content?.data?.title}</h1>
              <Tags>
                {props?.content?.data?.tags &&
                  props?.content?.data?.tags.map((tag, index: number) => (
                    <span key={index}>
                      {i18n.language.startsWith("ja")
                        ? tag?.nameJa
                        : tag?.nameEn}
                    </span>
                  ))}
              </Tags>
              <Player>
                {props?.content?.data?.youtubeUrl !== "" ? (
                  <ReactPlayer url={props?.content?.data?.youtubeUrl} />
                ) : (
                  <>
                    {props?.content?.data?.coverImage ? (
                      <div className="container">
                        <img
                          alt="article cover image"
                          src={props?.content?.data?.coverImage}
                        />
                      </div>
                    ) : (
                      ""
                    )}
                  </>
                )}
              </Player>
            </div>
            <Content id="content">
              {headings.length !== 0 && (
                <Sticky>
                  <TableOfContent
                    ghost
                    defaultActiveKey={["toc"]}
                    expandIconPosition="right"
                    expandIcon={({ isActive }) =>
                      isActive ? <UpIcon /> : <DownIcon />
                    }
                  >
                    <Collapse.Panel key="toc" header={"Table of contents"}>
                      <ul>
                        {headings.map((heading) => (
                          <li key={heading.id}>
                            <a
                              onClick={() => scrollToID(heading.id)}
                              className={
                                selectedMenu === heading.id
                                  ? "active"
                                  : undefined
                              }
                            >
                              {heading.title}
                            </a>
                          </li>
                        ))}
                      </ul>
                    </Collapse.Panel>
                  </TableOfContent>
                </Sticky>
              )}

              <div
                className="content"
                dangerouslySetInnerHTML={{
                  __html:
                    props?.content &&
                    JSON.parse(
                      props?.content?.data?.content.split("<p></p>").join("")
                    ),
                }}
              />
            </Content>
            <script
              id="my-script"
              async
              dangerouslySetInnerHTML={{
                __html: getNode(),
              }}
            />
          </>
        )}
        {data && data?.data?.id && <SimilarArticles slug={data?.data?.id} />}
      </Wrapper>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { id, admin } = context?.query;

  try {
    const content = await getArticleDetails(id, context?.locale, admin);
    return {
      props: {
        ...(await serverSideTranslations(context?.locale, ["common"])),
        content,
      },
    };
  } catch (error) {
    return {
      props: {
        error: "Internal server error",
      },
    };
  }
};

export default ArticleDetails;
